import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import LandingPage from "@/components/LandingPage";
import SignUp from "@/components/SignUp";
import PricingPage from "@/components/PricingPage";
import BestSellingProductListPage from "@/components/BestSellingProductListPage";
import CategoryCollectionPage from "@/components/CategoryCollectionPage";
import ProfilePage from "./components/ProfilePage";
import ProductDetail from "./components/ProductDetail";
import MarketingPage from "@/components/MarketingPage";
import cache from "utils/cache";
import PrivateRoutes from "routes/PrivateRoutes";
import PublicRoutes from "routes/PublicRoutes";
import { useVerifyUserQuery } from "api/authApi";
import { ALLOWED_ROLES } from "hooks/useAuthUser";
import Loader from "shared/Loader";
import OrdersDetailPage from "./components/OredrsDetailPage";
import OrderDetailPage from "@/components/OrderDetailPage";
import CartPage from "./components/CartPage";
import SupplierPage from "./components/SupplierPage";
import SupplierDashboardPage from "./components/SupplierDashboardPage";
import ProductListPage from "./components/ProductListPage";
import AddProductPage from "./components/AddProductPage";
import ThirdPartyProductsPage from "./components/ThirdPartyProductsPage";
import CheckoutPage from "./components/CheckoutPage";
import OrderSuccessPage from "./components/OrderSuccessPage";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import OrderPayment from "./components/CheckoutPage/OrderPayment";
import About from "./components/StaticPages/about";
import TermsOfService from "./components/StaticPages/terms&services";
import PrivacyPolicy from "./components/StaticPages/privacyPolicy";
import ShippingPolicy from "./components/StaticPages/shippingPolicy";
import ReturnPolicy from "./components/StaticPages/returnPolicy";
import { ShopifyCallbackPage } from "./pages/ShopifyCallbackPage";
import { EbayCallbackPage } from "./pages/EbayCallbackPage";
import ThankYouPage from "./components/ThankYouPage";
import { CartDrawerProvider } from "context/CartDrawerContext";

function App() {
  const { data: user, isLoading, isSuccess, isError } = useVerifyUserQuery();
  const clientID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  useEffect(() => {
    if (isLoading) return;

    if (isSuccess && user && ALLOWED_ROLES.includes(user.role)) {
      cache.setItem("user", user);
      return;
    }

    if (isError) {
      cache.removeItem("user");
    }
  }, [isLoading, isSuccess, isError, user]);

  if (isLoading) return <Loader isLoading={isLoading} />;

  // Initial options for the PayPal SDK
  const initialOptions = {
    "client-id": clientID,
    currency: "USD",
    "disable-funding": "paylater",
    vault: true,
  };

  return (
    <PayPalScriptProvider options={initialOptions}>
      <Router>
        <CartDrawerProvider>
        <Routes>
          <Route element={<PublicRoutes />}>
            <Route path="/" element={<MarketingPage />} />
            <Route path="/register" element={<MarketingPage />} />
          </Route>
          <Route element={<PrivateRoutes />}>
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/home" element={<LandingPage />} />
            <Route path="/my-orders" element={<OrdersDetailPage />} />
            <Route path="/my-orders/:orderId" element={<OrderDetailPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/supplier" element={<SupplierPage />} />
            <Route path="/supplier-dashboard" element={<SupplierDashboardPage />} />
            <Route path="/product_list_Page" element={<ProductListPage />} />
            <Route path="/add_product" element={<AddProductPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/success" element={<OrderSuccessPage />} />
            <Route path="/order_payment" element={<OrderPayment />} />
          </Route>
          <Route path="/about" element={<About />} />
          <Route path="/terms_services" element={<TermsOfService />} />
          <Route path="/privacy_policy" element={<PrivacyPolicy />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/return_policy" element={<ReturnPolicy />} />
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/thankyou" element={<ThankYouPage />} />
          <Route
            path="/best-selling-products"
            element={<BestSellingProductListPage />}
          />
          <Route path="/collection/:categorySlug" element={<CategoryCollectionPage />} />
          <Route
            path="/third-party-products"
            element={<ThirdPartyProductsPage />}
          />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/auth/shopify/callback" element={<ShopifyCallbackPage />} />
          <Route path="/auth/ebay/callback" element={<EbayCallbackPage />} />
          <Route path="*" element="Coming Soon" />
        </Routes>
        </CartDrawerProvider>
      </Router>
    </PayPalScriptProvider>
  );
}

export default App;
